import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import uniqueId from 'lodash/uniqueId';
import { TabContextProps, TabContextType, TabModel } from '../types';
import { scrollTabIntoView } from './helpers';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
import { ANIMATION_DURATION } from './constants';

export const TabContext = createContext<TabContextType>({} as TabContextType);

export const useTabContext = () => {
  const context = useContext(TabContext);
  return context;
};

export const TabContextProvider = ({
  defaultIndex,
  selectedIndex: outerSelectedIndex,
  onChange,
  variant,
  placement,
  isStretched,
  isLazy,
  autoSelect,
  tabGroupRef,
  children,
  onAnimationEnd,
  size,
}: TabContextProps) => {
  const idPrefixRef = useRef<string>();
  if (!idPrefixRef.current) {
    idPrefixRef.current = uniqueId('tabs-');
  }
  const isFocusedRef = useRef(false);
  const tabsRef = useRef<TabModel[]>([]);
  const [focusedTab, setFocusedTab] = useState<React.RefObject<HTMLButtonElement>>({ current: null });
  const [innerSelectedIndex, setInnerSelectedIndex] = useState(outerSelectedIndex ?? defaultIndex);
  const [isIndicatorRendered, setIsIndicatorRendered] = useState<boolean>(false);
  const selectedIndex = outerSelectedIndex ?? innerSelectedIndex;
  const setSelectedIndex = useCallback(
    (index: number) => {
      if (selectedIndex === index) return;
      if (index > -1 && tabsRef.current[index]) {
        if (tabsRef.current[index].isDisabled) return;
        tabsRef.current[index].wasSelected = true;
      }
      tabsRef.current.forEach((tab) => {
        tab.isSelected = tab.index === index;
      });
      if (typeof onAnimationEnd === 'function') {
        setTimeout(() => {
          onAnimationEnd?.();
        }, ANIMATION_DURATION);
      }
      onChange?.(index);
      if (typeof outerSelectedIndex !== 'number') {
        setInnerSelectedIndex(index);
      }
      scrollTabIntoView(index, placement, tabGroupRef.current, variant);
    },
    [selectedIndex, outerSelectedIndex, onChange, tabGroupRef, placement, variant, onAnimationEnd]
  );

  useKeyboardNavigation({
    placement,
    setSelectedIndex,
    autoSelect,
    tabsRef,
    tabGroupRef,
    isFocusedRef,
  });

  return (
    <TabContext.Provider
      value={{
        idPrefix: idPrefixRef.current,
        selectedIndex,
        setSelectedIndex,
        variant,
        placement,
        isStretched,
        isLazy,
        autoSelect,
        isFocusedRef,
        tabsRef,
        focusedTab,
        setFocusedTab,
        tabGroupRef,
        isIndicatorRendered,
        setIsIndicatorRendered,
        size,
      }}
    >
      {children}
    </TabContext.Provider>
  );
};
